import React, { Fragment, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import DocketAlerts from './DocketAlerts';
import useDataApi from 'utils/useDataApi';
import { next_time_it_is, user_timezone } from './DocketAlertTimeCalculations';
import request from 'superagent';
import Notification, { notify } from '../../components/common/Notification';
import { Alert, ProgressBar } from 'react-bootstrap';
import RPCProgressBar from '../../components/common/RPCProgressBar';


function DocketAlertsContainer({ court_code, case_number, uid }) {
  const [testStatus, setTestStatus] = useState({
    isTestSuccessful: false,
    testError: null,
    testProgressViewComponent: null,
  });

  const statusPanelEl = useRef(null);

  const [{ data, isLoading, isError }, { setData }] = useDataApi(
    `/rpc/cases/${court_code}/${case_number}/docket-alert/${uid}`, {}, {
      enabled: false,
      frequency: 'P1D',
      minimize_header: false,
      next_run: next_time_it_is(16), // 4pm default
      time_zone: user_timezone(),    // wherever user is
      report_settings: {
        docket_entry_filters: [],
        email_mode: 'new',
        email_distribution_list: [
          window.Drupal.settings.currentUserMail,
        ]
      }
    }
  );

  if (isLoading) return <ProgressBar now={100} active />;

  if (isError) return (
    <Alert bsStyle="danger">
      There has been an error loading Docket Alert settings.
      Please try again and contact support if the problem persists.
      <pre>
        {`${isError}`}
      </pre>
    </Alert>
  );

  const saveSettings = async (settings, clearTestResults = true) => {
    console.debug('Saving Docket Alert settings', settings);
    const method = settings.id ? 'patch' : 'post';
    try {
      setTestStatus({}); // make sure any old test results are gone
      const result = await request[method](`/rpc/cases/${court_code}/${case_number}/docket-alert/${uid}`)
        .send(settings);
      setData(result.body);
      notify.success('Docket Alert Saved');
    } catch (error) {
      notify.error(
        <span>
          We were unable to save your changes!
          <div style={{ border: '1px dotted maroon', padding: '4px', margin: '4px' }}>
            { `${error}` }
          </div>
          Please try again or contact support if problem persists.
        </span>
      );
      return DocketAlerts.saveErrorSignal(error);
    }
  };

  const testSettings = async (settings, testConfig) => {
    console.debug('Testing Docket Alert settings', settings);
    const method = settings.id ? 'patch' : 'post';
    try {
      setTestStatus({
        testProgressViewComponent: <ProgressBar label="Saving Alert..." active now={100} />
      });

      // Save current settings
      const result = await request[method](`/rpc/cases/${court_code}/${case_number}/docket-alert/${uid}`)
        .send(settings);

      // we need to save the id in settings so that the post-test request will do a PATCH
      if (method === 'post')
        settings.id = result.body.id;

      setTestStatus({
        testProgressViewComponent: <ProgressBar label="Alert saved, initiating test..." active now={100} />
      });

      const testResult = await request.post(`/rpc/cases/${court_code}/${case_number}/docket-alert/${uid}/execute`)
        .send(testConfig);

      console.log("Test Initiated: ", testResult);
      setTestStatus({
        testProgressViewComponent:
          <RPCProgressBar
            taskUrl={testResult.header.location}
            label="Updating Dockets..."
            onError={(errorObject) => {console.error("Docket Alert Test Error", errorObject); setTestStatus({ testError: errorObject ? errorObject.error || errorObject : 'Unknown Error' })} }
            onSuccess={async () => {
              console.info("Docket Alert Test Success",);
              setTestStatus({ isTestSuccessful: true });
              if (!settings.enabled) {
                console.log("Turning on alert since test passed");
                const saveErrors = await saveSettings({...settings, enabled: true}, false);
                if (!saveErrors) {
                  console.log('Scrolling status panel into view');
                  statusPanelEl.current.scrollIntoView({ behavior: 'smooth' });
                }
              }
            } }
          />,
      });

      setData(result.body);
    } catch (error) {
      console.log("Error Initiating Test", error);
      setTestStatus({ testError: error});
    }
  };


  return (
    <Fragment>
      <DocketAlerts
        statusPanelRef={statusPanelEl}
        settings={data}
        onSaveSettings={saveSettings}
        onTestAlert={testSettings}
        testStatus={testStatus}
      />
      <Notification />
    </Fragment>
  );
}


DocketAlertsContainer.propTypes = {
  court_code: PropTypes.string.isRequired,
  case_number: PropTypes.string.isRequired,
  uid: PropTypes.number.isRequired,
};


export default DocketAlertsContainer;



// WEBPACK FOOTER //
// ./src/CaseFolder/Settings/DocketAlertsContainer.jsx